import { Form } from '@/models/Form';
import { Entity } from '@/models/types/Entity';
import { useLayoutStore } from '@/store/layout/layoutStore';
import { entityTypes } from '@/enums/generic';
import { Pinia, Store } from '@/store/pinia-class-component';
import { useEntitiesStore } from '@/store/entities/entitiesStore';
import { Validation } from '@vuelidate/core';
import { generateDiff } from '@/utils/diff/diff';
import { Difference } from '@/utils/diff/Difference';
import { markRaw, toRaw, nextTick, Ref } from 'vue';

export enum FormAction {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
  REPLACE = 'REPLACE',
  MOUNT = 'MOUNT',
  UNMOUNT = 'UNMOUNT',
  TKD_SERVICE = 'TKD_SERVICE',
  REPAIR = 'REPAIR',
  REMAIN = 'REMAIN',
}

type TrackedStateRef = {
  ref: Ref<unknown>;
  initialSnapshot: Record<string, unknown> | null;
};

export function useFormStore() {
  return new FormStore();
}

@Store
export class FormStore extends Pinia {
  private _currentForm: Form;
  private _lastForm: Form;
  private _editNode?: Entity;
  private _formAction?: FormAction;
  private _validations: Validation[];
  private _trackedStateRefs: TrackedStateRef[];

  get currentForm(): Form {
    return this._currentForm;
  }

  get lastForm(): Form {
    return this._lastForm;
  }

  constructor() {
    super();
    this._currentForm = new Form();
    this._lastForm = new Form();
    this._formAction = undefined;
    this._validations = [];
    this._trackedStateRefs = [];
  }

  get validation(): Validation[] {
    return this._validations;
  }

  setValidation(v: Validation) {
    this._validations.push(v);
  }

  private deepClone(obj: unknown): Record<string, unknown> | null {
    if (obj == null) return null;
    const raw = toRaw(obj);
    return JSON.parse(JSON.stringify(raw));
  }

  trackStateRefs(refs: Ref<unknown>[]) {
    refs.forEach((ref) => {
      this._trackedStateRefs.push(
        markRaw({
          ref,
          initialSnapshot: null,
        })
      );
    });

    nextTick(() => {
      this._trackedStateRefs.forEach((tracked) => {
        tracked.initialSnapshot = this.deepClone(tracked.ref.value);
      });
    });
  }

  triggerValidation() {
    this.validation.forEach((v: Validation) => {
      v.$touch();
    });
  }

  resetValidation() {
    this.validation.forEach((v: Validation) => {
      v.$reset();
    });
  }

  resetChangeSnapshot() {
    this._trackedStateRefs = this._trackedStateRefs.map((tracked) =>
      markRaw({
        ref: tracked.ref,
        initialSnapshot: this.deepClone(tracked.ref.value),
      })
    );
  }

  get getForm() {
    return this._currentForm;
  }

  get formAction() {
    return this._formAction;
  }

  get isTKDService() {
    return this._formAction === FormAction.TKD_SERVICE;
  }

  setFormAction(action: FormAction) {
    this._formAction = action;
  }

  get editNode() {
    return this._editNode;
  }

  get hasFormChanges(): boolean {
    if (this._trackedStateRefs.length === 0) {
      return false;
    }

    let allDiffs: Difference[] = [];

    this._trackedStateRefs.forEach((tracked) => {
      const snapshot = tracked.initialSnapshot;
      const current = this.deepClone(tracked.ref.value);

      if (snapshot === null || current === null) {
        return;
      }

      const diffs = generateDiff(snapshot, current);
      allDiffs = [...allDiffs, ...diffs];
    });

    return allDiffs.length > 0;
  }

  isReplaceFormAction(): boolean {
    return this._formAction === FormAction.REPLACE;
  }

  isEditMode(): boolean {
    return !!this._currentForm.id;
  }

  changeCurrentNode(currentNode: Entity) {
    this._editNode = currentNode;
  }

  changeCurrentForm(form: Form) {
    if (form.setHistory) {
      const previousForm = new Form();
      const clonedObject = JSON.parse(JSON.stringify(this._currentForm));
      Object.assign(previousForm, clonedObject);
      this._lastForm = previousForm;
    }

    this._currentForm = form;
  }

  setToLastForm() {
    const lastForm = this._lastForm;
    lastForm.hasHistory(true);

    this.changeCurrentForm(lastForm);
  }

  setCurrentForm(form: Form) {
    this.changeCurrentForm(form);
  }

  resetCurrentForm() {
    this.changeCurrentForm(new Form());
    this._editNode = undefined;
    this._validations = [];
    this._trackedStateRefs = [];
  }

  setCurrentFormByEntity(
    entity: Entity,
    parentId = '',
    payload = {},
    history = false
  ) {
    if (entity.type === entityTypes.businessEntity) {
      return;
    }
    const entityParentId = parentId ? parentId : entity.getParentId();

    const form = new Form()
      .setName(entity.type)
      .setId(entity.id)
      .hasHistory(history)
      .setPayload(payload);

    if (entityParentId) {
      form.setParentId(entityParentId);
    }

    this.changeCurrentForm(form);
  }

  setCurrentFormByName(name: string, history?: boolean) {
    this.changeCurrentForm(
      new Form().setName(name).hasHistory(history ? history : false)
    );
  }

  createEntityForm(form: Form) {
    const layoutStore = useLayoutStore();
    layoutStore.closeFormDialog();
    layoutStore.openSideMenu();

    this.setCurrentForm(form);
  }

  createEntity(form: Form) {
    const layoutStore = useLayoutStore();

    this.setCurrentForm(form);
    layoutStore.openFormDialog();
    this.setFormAction(FormAction.CREATE);
  }

  editEntity(entity: Entity, parentId = '', payload = {}, history = false) {
    const layoutStore = useLayoutStore();
    layoutStore.openFormDialog();

    this.setCurrentFormByEntity(entity, parentId, payload, history);
    useFormStore().setFormAction(FormAction.EDIT);
  }

  editDevice(entity: Entity, parentId = '', payload = {}, history = false) {
    this.setCurrentFormByEntity(entity, parentId, payload, history);
    this.setFormAction(FormAction.EDIT);
    const entitiesStore = useEntitiesStore();
    if (
      entity?.activeDeviceId &&
      entitiesStore.hasEntityById(entity.activeDeviceId)
    ) {
      useFormStore().changeCurrentNode(
        entitiesStore.getEntityById(entity.activeDeviceId)
      );
    }
    if (
      entity?.activeTestDeviceId &&
      entitiesStore.hasEntityById(entity.activeTestDeviceId)
    ) {
      useFormStore().changeCurrentNode(
        entitiesStore.getEntityById(entity.activeTestDeviceId)
      );
    }

    useLayoutStore().openFormDialog();
  }

  mountDevice(entity: Entity, parentId = '', payload = {}, history = false) {
    this.setCurrentFormByEntity(entity, parentId, payload, history);
    this.setFormAction(FormAction.MOUNT);
    useLayoutStore().openFormDialog();
  }

  replaceDevice(entity: Entity) {
    this.setCurrentFormByEntity(entity, undefined, {}, false);
    this.setFormAction(FormAction.REPLACE);
    useFormStore().changeCurrentNode(
      useEntitiesStore().getEntityById(entity?.activeDeviceId)
    );
    useLayoutStore().openFormDialog();
  }

  unmountDevice(entity: Entity, parentId = '') {
    this.setCurrentFormByEntity(entity, parentId, {}, false);
    this.setFormAction(FormAction.UNMOUNT);
    useFormStore().changeCurrentNode(
      useEntitiesStore().getEntityById(entity?.activeDeviceId)
    );
    useLayoutStore().openFormDialog();
  }

  performTkdService(entity: Entity, parentId = '', showReport = false) {
    this.setCurrentFormByEntity(entity, parentId, { showReport }, false);
    this.setFormAction(FormAction.TKD_SERVICE);
    useLayoutStore().openFormDialog();
  }

  showCurrentFormList(type: string) {
    const layoutStore = useLayoutStore();

    const currentNodeId = layoutStore.currentNode?.id;
    if (!currentNodeId) {
      throw new Error('No current node id');
    }

    this.setCurrentForm(new Form().setName(type).setParentId(currentNodeId));
    layoutStore.openFormDialog();
  }
}
